import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet, registerLocaleData } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, LOCALE_ID, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { NotificationGroup, Notification } from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil } from 'rxjs';
import { SharedComponentModule } from '@shared/components/shared-component.module';
import localeEs from '@angular/common/locales/es';

import { MatPaginatorModule } from '@angular/material/paginator';

registerLocaleData(localeEs);

@Component({
    selector       : 'notifications',
    templateUrl    : './notifications.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'notifications',
    standalone     : true,
    imports        : [MatButtonModule, NgIf, MatIconModule, MatTooltipModule, NgFor, NgClass, NgTemplateOutlet, RouterLink, DatePipe, SharedComponentModule, MatPaginatorModule],
    providers      : [{ provide: LOCALE_ID, useValue: 'es-ES' }],
    animations     : fuseAnimations
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    color_primario: string;
    color_secundario: string;
    default_color: string = 'bg-primary';
    notifications_group: NotificationGroup[];
    notifications: Notification[];
    grupoSeleccionado: NotificationGroup;
    showNotifications = false;
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _empresaService: EmpresaService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to notification changes
        this._notificationsService.notificationsGroup$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notificationsGroup: NotificationGroup[]) =>
            {
                // Load the notifications
                this.notifications_group = notificationsGroup;

                // Calculate the unread count
                this._calculateUnreadCount();

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to empresa data
        this._empresaService.empresa$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(empresa => {
                this.color_primario = empresa.color_primario;
                this.color_secundario = empresa.color_secundario;
                this._changeDetectorRef.markForCheck();
            });
        // Load empresa data
        this._empresaService.getEmpresa();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void
    {
        // Mark all as read
        this._notificationsService.markAllAsRead().subscribe();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: NotificationGroup): void
    {
        // Toggle the read status
        notification.read = !notification.read;

        // Update the notification
        this._notificationsService.update(notification.id, notification).subscribe();
    }

    /**
     * Delete the given notification
     */
    delete(notification: NotificationGroup): void
    {
        // Delete the notification
        this._notificationsService.delete(notification.id).subscribe();
    }

    // Paginación
    pageSize: number = 5;
    pageIndex: number = 0;
    paginatedNotifications: Notification[] = [];

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() =>
        {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the count of alerts
     *
     * @private
     */
    private _calculateUnreadCount(): void
    {
        let count = 0;

        if ( this.notifications_group && this.notifications_group.length )
        {
            // Sumar la cantidad de ítems que contiene cada grupo para mostrar el número real de alertas pendientes
            count = this.notifications_group.length;
        }

        this.unreadCount = count;
    }

    /**
     * 
     * @param notification 
     * Este metodo mustra y filtra las notificaciones que se tiene de las Notificaciones Agrupadas
     */
    verMasNotificaciones(notification: NotificationGroup): void {
        // Almacena el grupo seleccionado
        this.grupoSeleccionado = notification;
        this.pageIndex = 0;

        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {
                // Filtra las notificaciones según el grupo seleccionado
                this.notifications = notifications.filter(n => n.id === this.grupoSeleccionado.id);
                this.updatePaginatedNotifications();
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
        this.showNotifications = true;
    }

    /**
     * Cambiar de página en la paginación
     */
    onPageChange(event: any): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.updatePaginatedNotifications();
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Actualiza el listado paginado en base al index y tamaño
     */
    updatePaginatedNotifications(): void {
        const startIndex = this.pageIndex * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.paginatedNotifications = this.notifications.slice(startIndex, endIndex);
    }
}
